import React, { useCallback, useEffect, useState, useMemo } from 'react';
import AIUtils from '../../../../../services/AIUtils';
import _ from 'lodash';
import moment from 'moment-timezone';
import { IconButton } from '@material-ui/core';
import Icon from 'components/Utils/NewIcon';
import './style.scss';
import DefaultChip from '../../../../Utils/DefaultChip';
import { store, useAppSelector } from '../../../../../careand-redux/store';
import useAssistant from '../../../../../hooks/useAssistant';
import { useAppDispatch } from '../../../../../careand-redux/useAppDispatch';
import { uiActions } from '../../../../../careand-redux/slices/ui/ui';
import { api } from '../../../../../careand-redux/api/ApiThunks';
import { Appointment, DocumentTemplate } from '../../../../../generated/api';
import { useCPPContext } from '../../../../Patient/CPP/CPPContext';
import { runSavedPromptTyped } from '../../../../../thunks/runSavedPromptTyped';

const CACHE_TTL = 600_000; //10 min

type Props = {
  appointment: Appointment;
  onClick: (template: DocumentTemplate) => void;
};

function DocumentTemplateSuggestions(props: Props) {
  const { loading, suggestedTemplates, generateTemplateSuggestions, invalidCache } =
    useGenerateTemplateSuggestions(props.appointment);

  useEffect(() => {
    if (invalidCache) {
      generateTemplateSuggestions();
    }
  }, [generateTemplateSuggestions, invalidCache]);

  return (
    <div className="template-suggestions-section m-4">
      <div className="suggestions-header">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Icon src="sparkles" width="16px" style={{ marginRight: '8px' }} />
          <span className="small-title">Template Suggestions</span>
        </div>
        <IconButton onClick={() => generateTemplateSuggestions()} size="small">
          <Icon
            src="rotate"
            style={{ cursor: 'pointer' }}
            loading={loading}
            alt="Refresh suggestions"
            showTooltip={true}
          />
        </IconButton>
      </div>
      <div className="suggestions-list">
        {suggestedTemplates.map((template) => (
          <DefaultChip
            key={template.id}
            text={template.description}
            backgroundColor="#42bcdd"
            textColor="white"
            onClick={() => props.onClick(template)}
          />
        ))}
      </div>
    </div>
  );
}

export function useGenerateTemplateSuggestions(appointment: Appointment) {
  const appointmentId = appointment.id!;
  const patientId = appointment.patient_id!;

  const dispatch = useAppDispatch();
  const { unreactiveNoteValue } = useCPPContext();
  const { runSavedPromptTyped } = useAssistant('template-suggestions-generator');
  const now = useMemo(() => moment().unix(), []);
  const cache = useAppSelector(
    (state) => state.ui.templateSuggestionsByAppointmentId[appointmentId],
  );
  const invalidCache = !cache || now - cache.date >= CACHE_TTL;
  const templateIds = cache?.templateIds;
  const isLoading = cache?.isLoading ?? false;
  const templates = useAppSelector((state) => state.templates.templates);

  const suggestedTemplates = useMemo(() => {
    return _(templateIds ?? [])
      .map((templateId) => templates.find((it) => it.id === templateId))
      .compact()
      .value();
  }, [templateIds, templates]);

  const setLoading = useCallback(
    (isLoading: boolean) => {
      dispatch(
        uiActions.updateTemplateSuggestions({ isLoading, appointmentId, date: moment().unix() }),
      );
    },
    [dispatch, appointmentId],
  );

  const setTemplateSuggestions = useCallback(
    (templateIds: string[]) => {
      dispatch(
        uiActions.updateTemplateSuggestions({ appointmentId, templateIds, date: moment().unix() }),
      );
    },
    [dispatch, appointmentId],
  );

  const resetTemplateSuggestions = useCallback(() => {
    dispatch(uiActions.updateTemplateSuggestions({ appointmentId, templateIds: [] }));
  }, [dispatch, appointmentId]);

  const generateTemplateSuggestions = useCallback(async () => {
    resetTemplateSuggestions();

    try {
      let transcription = '';

      if (appointmentId) {
        const res = await dispatch(
          api.Appointments.getAppointmentTranscription({ id: appointmentId }),
        );
        const text = res?.messages?.map((msg) => `${msg.from}: ${msg.text}`)?.join('\n');
        if (text && text.length > 0) {
          transcription = text;
        }
      }

      const noteOrDraft =
        unreactiveNoteValue.current ??
        (await (async () => {
          const drafts = (await dispatch(api.HealthRecords.getOwnDrafts())) ?? [];
          return _.orderBy(drafts, ['date'], ['desc'])[0]?.note.value.value ?? '';
        })());

      setLoading(true);

      const queries = await runSavedPromptTyped(
        'Generate Query For Document Search',
        {
          note: noteOrDraft,
          transcription: transcription || 'No Transcription',
        },
        { patientId },
      );

      if (queries) {
        const MAX_SUGGESTIONS = 5;

        // Calculate how many to take from each query
        const perQuery = Math.ceil(MAX_SUGGESTIONS / queries.length);
        const allResults = await Promise.all(
          queries.map(async (query) => {
            const res = await dispatch(
              api.Templates.searchDocumentTemplates({ stringResource: { value: query } }),
            );
            return res?.slice(0, perQuery) || [];
          }),
        );

        // Flatten and dedupe by id, then take max 5
        const seen = new Set<string>();
        const suggestions = allResults
          .flat()
          .filter((template) => {
            if (!template.id || seen.has(template.id)) return false;
            seen.add(template.id);
            return true;
          })
          .slice(0, MAX_SUGGESTIONS);

        setTemplateSuggestions(suggestions.map((template) => template.id || 'n/a'));
      }
    } finally {
      setLoading(false);
    }
  }, [
    runSavedPromptTyped,
    appointment,
    patientId,
    dispatch,
    appointmentId,
    setLoading,
    resetTemplateSuggestions,
    unreactiveNoteValue,
  ]);

  const result = useMemo(() => {
    return {
      generateTemplateSuggestions,
      loading: isLoading,
      templateIds: templateIds ?? [],
      suggestedTemplates,
      invalidCache,
    } as const;
  }, [generateTemplateSuggestions, isLoading, templateIds, suggestedTemplates, invalidCache]);

  return result;
}

export default DocumentTemplateSuggestions;
